import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { Check, Loader2, XCircle, Package } from 'lucide-react';
import api from '../services/api';
import { trackPurchase, isPurchaseAlreadyTracked, markPurchaseTracked } from '../utils/analytics';

const CheckoutSuccessPage = () => {
  const { currentLanguage, t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { loadCart } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [pollAttempts, setPollAttempts] = useState(0);

  const content = {
    en: {
      processing: "Processing your payment...",
      success: "Payment Successful!",
      thankYou: "Thank you for your order",
      orderNumber: "Order Number",
      emailSent: "A confirmation email has been sent to your email address.",
      viewOrders: "View Orders",
      continueShopping: "Continue Shopping",
      error: "Payment Error",
      errorMessage: "There was an issue processing your payment. Please contact support.",
      tryAgain: "Try Again"
    },
    es: {
      processing: "Procesando su pago...",
      success: "¡Pago Exitoso!",
      thankYou: "Gracias por su pedido",
      orderNumber: "Número de Pedido",
      emailSent: "Se ha enviado un correo de confirmación a su dirección de email.",
      viewOrders: "Ver Pedidos",
      continueShopping: "Continuar Comprando",
      error: "Error de Pago",
      errorMessage: "Hubo un problema al procesar su pago. Por favor contacte soporte.",
      tryAgain: "Intentar de Nuevo"
    },
    ru: {
      processing: "Обработка платежа...",
      success: "Оплата Успешна!",
      thankYou: "Спасибо за ваш заказ",
      orderNumber: "Номер Заказа",
      emailSent: "Письмо с подтверждением отправлено на ваш email.",
      viewOrders: "Посмотреть Заказы",
      continueShopping: "Продолжить Покупки",
      error: "Ошибка Оплаты",
      errorMessage: "Возникла проблема с обработкой платежа. Пожалуйста, свяжитесь с поддержкой.",
      tryAgain: "Попробовать Снова"
    }
  };

  const c = content[currentLanguage] || content.en;

  const pollPaymentStatus = useCallback(async (sessionId) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (pollAttempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please check your orders.');
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        if (data.order_id) {
          // Fetch order details
          const orderResponse = await api.get(`/orders/${data.order_id}`);
          setOrder(orderResponse.data);

          // GA4 `purchase` + Yandex.Metrica ecommerce.purchase +
          // reachGoal('purchase'). This is the MOST important event in
          // the entire funnel, Google Ads' Performance Max + DV360
          // smart-bidding optimise against it directly, and Yandex's
          // ranking algorithm factors in completed conversions per
          // locale. Single-flight guarded against React StrictMode +
          // payment-poll retries (data.order_id stays stable, the same
          // session_id could resolve twice if the user refreshes).
          try {
            if (!isPurchaseAlreadyTracked(data.order_id)) {
              trackPurchase(orderResponse.data, currentLanguage);
              markPurchaseTracked(data.order_id);
            }
          } catch (_) { /* analytics never blocks UX */ }
        }
        // Refresh cart (should be empty now)
        await loadCart();
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired.');
      } else {
        // Continue polling
        setPollAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
      setError('Failed to verify payment status.');
    }
  }, [pollAttempts, loadCart, currentLanguage]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      navigate('/');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout/success?session_id=' + sessionId);
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-pink-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{c.processing}</h2>
              <p className="text-gray-600">{t('checkout_success_please_wait')}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{c.success}</h2>
              <p className="text-gray-600 mb-6">{c.thankYou}</p>
              
              {order && (
                <div className="bg-pink-50 rounded-xl p-6 mb-8">
                  <p className="text-sm text-gray-600 mb-1">{c.orderNumber}</p>
                  <p className="text-2xl font-bold text-pink-600 mb-4">{order.id}</p>
                  
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Package className="w-5 h-5" />
                    <span className="font-medium">€{order.total?.toFixed(2)}</span>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-gray-500 mb-8">{c.emailSent}</p>
              
              <div className="flex gap-4 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/products')}
                  className="px-6"
                >
                  {c.continueShopping}
                </Button>
                <Button 
                  onClick={() => navigate('/orders')}
                  className="bg-pink-500 hover:bg-pink-600 text-white px-6"
                >
                  {c.viewOrders}
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{c.error}</h2>
              <p className="text-gray-600 mb-8">{error || c.errorMessage}</p>
              
              <div className="flex gap-4 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/products')}
                >
                  {c.continueShopping}
                </Button>
                <Button 
                  onClick={() => navigate('/orders')}
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                >
                  {c.viewOrders}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
